<template>
  <v-card flat>
    <v-card-title>Add Income</v-card-title>
    <v-card-text>
      <v-form @submit.prevent="submitForm">
        <v-row>
          <v-col cols="12" md="4">
            <v-select
              v-model="form.adi"
              :items="adiList"
              item-value="adi_id"
              label="Driving Instructor"
              :error-messages="errors.adi"
              @blur="$v.form.adi.$touch()"
              @change="
                $v.form.adi.$touch();
                loadADITransactions();
              "
              outlined
              dense
              clearable
            >
              <template v-slot:item="{ item }">
                <strong> A{{ item.adi_id }}</strong> &nbsp;-&nbsp;
                {{ item.title }}
                {{ item.fname }}
                {{ item.lname }}
              </template>
              <template v-slot:selection="{ item }">
                <strong> A{{ item.adi_id }}</strong
                >&nbsp;-&nbsp; {{ item.title }}
                {{ item.fname }}
                {{ item.lname }}
              </template>
            </v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="form.transactionType"
              :items="getIncomeCategores"
              item-text="category"
              item-value="rec_id"
              label="Income Type *"
              :error-messages="errors.transactionType"
              @blur="$v.form.transactionType.$touch()"
              @change="$v.form.transactionType.$touch()"
              outlined
              dense
            ></v-select>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="form.date"
              label="Date *"
              type="date"
              :error-messages="errors.date"
              @blur="$v.form.date.$touch()"
              outlined
              dense
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model="form.amount"
              label="Amount (£) *"
              type="number"
              step="0.01"
              min="0"
              prefix="£"
              :error-messages="errors.amount"
              @blur="$v.form.amount.$touch()"
              outlined
              dense
            ></v-text-field>
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              v-model="form.method"
              :items="paymentMethods"
              label="Payment Method *"
              :error-messages="errors.method"
              @blur="$v.form.method.$touch()"
              @change="$v.form.method.$touch()"
              outlined
              dense
            ></v-select>
          </v-col>

          <v-col cols="12" md="4">
            <v-select
              v-model="form.status"
              :items="statuses"
              label="Status *"
              :error-messages="errors.status"
              @blur="$v.form.status.$touch()"
              @change="$v.form.status.$touch()"
              outlined
              dense
            ></v-select>
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="form.notes"
              label="Notes"
              :error-messages="errors.notes"
              @blur="$v.form.notes.$touch()"
              outlined
              dense
              rows="3"
            ></v-textarea>
          </v-col>
        </v-row>

        <v-btn
          type="submit"
          color="success"
          :loading="loading"
          :disabled="$v.form.$invalid"
        >
          <v-icon left>mdi-plus</v-icon>
          Add Income
        </v-btn>
      </v-form>
    </v-card-text>
    <v-divider class="mx-4"></v-divider>
    <v-card-title v-if="form.adi"
      >Income Records - {{ getADIName }}
    </v-card-title>
    <v-card-title v-else>Income Records - School</v-card-title>
    <v-card-text>
      <TaxYearSelector
        @export="exportTransaction"
        :exportFomatsList="expFomatsList"
      />
    </v-card-text>
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="showSchoolData"
        :search="keyword"
        class="elevation-1"
        :loading="loading"
        loading-text="Loading... Please wait"
      >
        <template v-slot:[`item.t_date`]="{ item }">
          <span>{{ item.t_date | dateReOrder }}</span>
        </template>
        <template v-slot:[`item.adi_name`]="{ item }">
          <span v-if="!item.adi_name">{{ getADIName }}</span>
          <span v-else>{{ item.adi_name }} (A{{ item.adi_id }})</span>
        </template>

        <template v-slot:[`item.t_amount`]="{ item }">
          <span>{{ item.t_amount | currencyStyle }}</span>
        </template>
        <template v-slot:[`item.t_status`]="{ item }">
          <span style="text-transform: capitalize">{{ item.t_status }}</span>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { required, minValue, maxLength } from "vuelidate/lib/validators";
import { mapActions, mapGetters } from "vuex";
import {
  GET_FM_CATEGORIES,
  M_OU,
  GET_ACTIVE_INSTRUCTORS,
  LOAD_FM_TRANSACTION,
  SAVE_FM_TRANSACTION,
  IS_SCHOOL_FM_TRANSACTION_LOADED,
  LOAD_SCHOOL_FM_TRANSACTION,
  GET_SCHOOL_INCOME_FM_TRANSACTION,
  DOWNLOAD_EXPORT_FILE,
  M_DD,
  GET_ADI_PAYMENT_METHODS,
} from "@/store/mutation-list";
import TaxYearSelector from "@/components/FinancialManagement/TaxYearSelector.vue";

export default {
  name: "IncomeForm",
  components: { TaxYearSelector },

  data() {
    return {
      loading: false,
      form: {
        adi: "",
        transactionType: "",
        date: "",
        amount: "",
        method: "",
        status: "",
        notes: "",
      },

      statuses: [
        { text: "Completed", value: "completed" },
        { text: "Pending", value: "pending" },
        { text: "Failed", value: "failed" },
        { text: "Cancelled", value: "cancelled" },
      ],
      tblLoading: false,
      keyword: "", // the seaching function,
      headers: [
        { text: "Date", value: "t_date" },
        { text: "Type", value: "cat_name" },
        { text: "ADI", value: "adi_name" },
        { text: "Amount", value: "t_amount" },
        { text: "Status", value: "t_status" },
        { text: "Method", value: "t_pay_method" },
        { text: "Notes", value: "t_notes" },
        // { text: "Action", value: "" },
      ],
      adiTransactionData: [],
      expFomatsList: ["CSV", "Excel", "Quickbooks", "Sage", "Xero"],
    };
  },

  validations: {
    form: {
      adi: {
        required,
      },
      transactionType: {
        required,
      },
      date: {
        required,
      },
      amount: {
        required,
        minValue: minValue(0.01),
      },
      method: {
        required,
      },
      status: {
        required,
      },
      notes: {
        maxLength: maxLength(250),
      },
    },
  },

  computed: {
    ...mapGetters({
      listCategories: `${M_OU}${GET_FM_CATEGORIES}`,
      adiList: `${M_OU}${GET_ACTIVE_INSTRUCTORS}`,
      isTransactionLoaded: `${M_OU}${IS_SCHOOL_FM_TRANSACTION_LOADED}`,
      schoolTransactions: `${M_OU}${GET_SCHOOL_INCOME_FM_TRANSACTION}`,
      dbPaymentMethods: `${M_DD}${GET_ADI_PAYMENT_METHODS}`,
    }),
    getIncomeCategores() {
      if (!this.listCategories) return [];
      return this.listCategories.filter((ar) => ar.type === "income");
    },
    getADIName() {
      if (this.form.adi && this.adiList.length > 0) {
        const adi_details = this.adiList.find(
          (adi) => adi.adi_id === this.form.adi
        );
        return ` ${adi_details.fname} ${adi_details.lname} (A${this.form.adi})`;
      }
      return "";
    },
    showSchoolData() {
      if (!this.form.adi) {
        //then show shool data.
        return this.schoolTransactions;
      } else {
        return this.adiTransactionData;
      }
    },
    paymentMethods() {
      if (!this.dbPaymentMethods) {
        return [];
      }

      let retAr = [];
      this.dbPaymentMethods.forEach((rec) => {
        retAr.push({ text: rec.txt, value: rec.txt });
      });
      return retAr;
    },
    errors() {
      const errors = {
        adi: [],
        transactionType: [],
        date: [],
        amount: [],
        method: [],
        status: [],
        notes: [],
      };

      if (this.$v.form.adi.$dirty) {
        !this.$v.form.adi.required &&
          errors.adi.push("Driving instructor is required");
      }

      if (this.$v.form.transactionType.$dirty) {
        !this.$v.form.transactionType.required &&
          errors.transactionType.push("Income type is required");
      }

      if (this.$v.form.date.$dirty) {
        !this.$v.form.date.required && errors.date.push("Date is required");
      }

      if (this.$v.form.amount.$dirty) {
        !this.$v.form.amount.required &&
          errors.amount.push("Amount is required");
        !this.$v.form.amount.minValue &&
          errors.amount.push("Amount must be greater than 0");
      }

      if (this.$v.form.method.$dirty) {
        !this.$v.form.method.required &&
          errors.method.push("Payment method is required");
      }

      if (this.$v.form.status.$dirty) {
        !this.$v.form.status.required &&
          errors.status.push("Status is required");
      }

      if (this.$v.form.notes.$dirty) {
        !this.$v.form.notes.maxLength &&
          errors.notes.push("Notes must be less than 500 characters");
      }

      return errors;
    },
  },

  methods: {
    ...mapActions({
      saveTransaction: `${M_OU}${SAVE_FM_TRANSACTION}`,
      getTranscations: `${M_OU}${LOAD_FM_TRANSACTION}`,
      loadAllTransactions: `${M_OU}${LOAD_SCHOOL_FM_TRANSACTION}`,
      dwExportFile: `${M_OU}${DOWNLOAD_EXPORT_FILE}`,
    }),
    async submitForm() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) return;

      this.saveTransaction({
        adi: this.form.adi,
        type: this.form.transactionType,
        date: this.form.date,
        amount: this.form.amount,
        method: this.form.method,
        status: this.form.status,
        notes: this.form.notes,
      }).then((res) => {
        //response from store. All complete just need to show to popup message
        if (res == "SUCCESS") {
          this.$fire({
            title: "Success",
            text: "Income recorded successfuly.",
            type: "success",
            timer: 3000,
          }).then((r) => {
            console.log(r.value);
          });
          this.resetForm();
          this.dialog = false;
        } else {
          this.$fire({
            title: "Error",
            text: "Error recording Income. Please try again.",
            type: "error",
            allowOutsideClick: false,
            //timer: 3000,
          }).then((r) => {
            console.log(r.value);
          });
        }
      });
    },
    loadADITransactions() {
      this.tblLoading = true;
      this.adiTransactionData = [];
      let tempOBj = [];
      this.getTranscations({
        adiid: this.form.adi,
        type: "income",
      })
        .then((res) => {
          if (res) {
            tempOBj = res;
            // console.log(tempOBj);
            if ("result" in tempOBj) {
              this.adiTransactionData = [];
            } else {
              this.adiTransactionData = res;
            }
          } else {
            this.adiTransactionData = [];
          }
        })
        .finally(() => {
          this.tblLoading = false;
        });
    },
    //this catches the export event
    exportTransaction({ start_date, end_date, exp_format }) {
      //lets send the reqirenment to store to get the file
      //reqirnments are
      //start date, end date, export format, records of school or adi, data type i.e. income,expense or milage
      let owner = "school";
      let ext = ".csv";

      if (this.form.adi != "" || this.form.adi != null) {
        owner = this.form.adi;
      }
      switch (exp_format) {
        case "CSV":
          ext = "export.csv";
          break;
        case "Excel":
          ext = "export.xlsx";
          break;
        /*case "PDF":
          ext = "export.pdf";
          break;*/

        default:
          ext = "export.ofx";
          break;
      }

      this.dwExportFile({
        owner: owner,
        start: start_date,
        end: end_date,
        type: "income",
        exp_format: exp_format,
      })
        .then((rep) => {
          //create file link in the browser's memeory
          const href = URL.createObjectURL(rep);
          // create 'a' HTML element with the ref to the file & click
          const link = document.createElement("a");
          link.href = href;
          // link.setAttribute("download", "file.csv");
          // link.setAttribute("download", "file.xlsx");
          link.setAttribute("download", ext);
          document.body.appendChild(link);
          link.click();

          // clean up 'a' element and remove objectURL
          document.body.removeChild(link);
          URL.revokeObjectURL(href);
        })
        .finally(() => {});
    },

    resetForm() {
      const today = new Date().toISOString().substr(0, 10);
      this.form = {
        transactionType: "",
        date: today,
        amount: "",
        method: "",
        status: "",
        notes: "",
      };
      this.adiTransactionData = [];
      this.$v.form.$reset();
    },
  },

  mounted() {
    // Set default date to today
    const today = new Date().toISOString().substr(0, 10);
    this.form.date = today;

    // lets load all data if not already loaded
    if (!this.isTransactionLoaded) {
      this.loadAllTransactions();
      //load transactions
      this.adiTransactionData = this.schoolTransactions;
    }
  },
};
</script>
